import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/services/local/toast.service';
import { EmailsService } from '../entity/emails.service';
import { RealtimeService } from './realtime.service';

/**
 * The due-Wiedervorlagen counter behind the navigation badge. Deliberately built on the ONE
 * existing follow-ups endpoint (Entscheidungslog: ein Konsument, ein Code-Pfad — the list
 * build owns the auto-clear logic, a separate count query would drift). Refreshes on a slow
 * poll plus every realtime email event, and toasts when the due count RISES while the app
 * is open — new urgency should be noticed, not discovered.
 */
@Injectable({
    providedIn: 'root'
})
export class FollowUpBadgeService {

    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly emailsService = inject(EmailsService);

    private readonly realtime = inject(RealtimeService);

    private readonly toast = inject(ToastService);

    private readonly t = inject(TranslateService);

    private readonly destroyRef = inject(DestroyRef);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** The number of DUE follow-ups (emails + tasks) — the navigation badge reads this. */
    readonly dueCount = signal<number>(0);

    /** Whether at least one load completed — the toast never fires on the very first count. */
    private hasLoaded = false;

    /** Coalesces bursts of realtime events into one refresh. */
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        this.refresh();

        // Slow poll: entries BECOME due purely by time passing — no event will ever fire
        const interval = setInterval(() => this.refresh(), 5 * 60 * 1000);

        // Realtime: any email event can create, answer or clear a Wiedervorlage
        const unsubscribe = this.realtime.on('*', (event) => {
            if (event.type.startsWith('email.') || event.type.startsWith('task.')) {
                this.scheduleRefresh();
            }
        });

        this.destroyRef.onDestroy(() => {
            clearInterval(interval);
            unsubscribe();
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ METHODS
    // -----------------------------------------------------------------------------------------------------

    /** Reloads the follow-ups and recounts the due ones — also toasts on a RISING count. */
    refresh(): void {
        this.emailsService.getFollowUps().subscribe({
            next: (items) => {
                const due = items.filter((item) => item.isOverdue).length;
                const previous = this.dueCount();

                this.dueCount.set(due);

                if (this.hasLoaded && due > previous) {
                    this.toast.add(
                        this.t.instant('Email.FollowUps.DueToast', { count: due }),
                        { status: 'warning' });
                }

                this.hasLoaded = true;
            },
            // The badge is a convenience — a failing count must never toast errors around
            error: () => undefined,
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    /** Debounced refresh — a sync run fires dozens of events, one reload suffices. */
    private scheduleRefresh(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        this.refreshTimer = setTimeout(() => {
            this.refreshTimer = null;
            this.refresh();
        }, 3000);
    }
}
